import {
  ClearableBy,
  Severity,
  type Fault,
} from '@sb-protogen/external/external_control';
import type { TCErrorCode } from '@sb-protogen/messenger/trajectory_controller_service';

import { buildFault, type FaultContext } from './mapTcFault';
import { COMMAND_PATH_ONLY_TC_CODES, tcErrorCode } from './tcErrorCodes';

/**
 * A trajectory-controller code arriving on the **command** path: the request was
 * refused and nothing moved.
 *
 * The codes in `COMMAND_PATH_ONLY_TC_CODES` appear only in synchronous RPC
 * replies on the command path. The jog path reaches this mapper through
 * `tcRejectionFault` (`libs/routine-runner/util/tcOperatorFaultMessage.ts`) when
 * a SpeedJ/SpeedL enqueue is refused; the rest of the vocabulary is defined here
 * so the channel distinction stays explicit.
 *
 * `REJECTED` is correct here and wrong on the fault path, which is the entire
 * reason there are two mappers keyed on the channel rather than one keyed on the
 * code.
 *
 * Note for whoever builds the command path: `TC_ERROR_QUEUE_FULL` is already
 * dead on the wire. A full queue replies `{accepted=false, error=NONE}` and
 * `waitAndFillReply` maps `NONE → INVALID_ARGUMENT`, so queue-full is reported
 * as `INVALID_ARGUMENT`. That defect is not this mapper's to fix.
 */
export function mapTcRejection(
  code: TCErrorCode,
  context?: FaultContext,
): Fault {
  return buildFault(
    tcErrorCode(code),
    Severity.REJECTED,
    ClearableBy.CLEARABLE_BY_CLIENT,
    context,
  );
}

export function isCommandPathOnlyTcCode(code: TCErrorCode): boolean {
  return COMMAND_PATH_ONLY_TC_CODES.includes(code);
}
